import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ServiceCardComponent, ServiceOrder } from '../shared/components/service-card/service-card.component';
import { ProfileMenuComponent } from '../shared/components/profile-menu/profile-menu.component';
import { LoaderComponent } from '../shared/components/loader/loader.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, Subscription, take, tap } from 'rxjs';
import { AuthService } from '../shared/services/apis/auth.service';
import { ServiceOrderApiService } from '../shared/services/apis/service-order-api.service';
import { ServiceOrderListItemDto } from '../shared/services/responses/service-order-list-item.dto';

@Component({
    selector: 'app-home',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, ServiceCardComponent, ProfileMenuComponent, LoaderComponent],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit, OnDestroy {
    activeTab: 'abertos' | 'ultima-semana' | 'rascunhos' = 'rascunhos';
    searchControl = new FormControl('', { nonNullable: true });
    activeStatusFilter = 'Todos';
    availableStatusFilters: string[] = ['Todos'];
    isMenuOpen = false;
    isLoadingOrders = false;
    isLoadingMore = false;
    isSearchPending = false;
    loadOrdersError = '';
    isPulling = false;
    isSnapping = false;
    pullDistance = 0;
    private loadOrdersSubscription?: Subscription;
    private searchSubscription?: Subscription;
    private lastLoadKey = '';
    private readonly defaultPageSize = 20;
    private currentPage = 1;
    private readonly PULL_THRESHOLD = 70;
    private touchStartY = 0;
    private isTouchActive = false;
    private touchMoveHandler!: (e: TouchEvent) => void;
    hasNextPage = false;

    serviceOrders: ServiceOrder[] = [];
    filteredOrders: ServiceOrder[] = [];

    constructor(
        private router: Router,
        private authService: AuthService,
        private serviceOrderApiService: ServiceOrderApiService,
        private cdr: ChangeDetectorRef
    ) {
        this.searchSubscription = this.searchControl.valueChanges.pipe(
            map(value => value.trim()),
            tap((search) => {
                this.isSearchPending = search.length >= 3;
                this.cdr.markForCheck();
            }),
            filter(value => value.length === 0 || value.length >= 3),
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe((search) => {
            this.loadOrdersByActiveTab(1, 20, search);
        });

        this.loadOrdersByActiveTab();
    }

    get readyToRefresh(): boolean {
        return this.pullDistance >= this.PULL_THRESHOLD;
    }

    ngOnInit() {
        // touchmove must be non-passive to allow preventDefault (stops native overscroll)
        this.touchMoveHandler = (e: TouchEvent) => this.onTouchMove(e);
        window.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    }

    @HostListener('touchstart', ['$event'])
    onTouchStart(event: TouchEvent) {
        if (window.scrollY > 0 || this.isLoadingOrders) return;
        this.touchStartY = event.touches[0].clientY;
        this.isTouchActive = true;
    }

    onTouchMove(event: TouchEvent) {
        if (!this.isTouchActive || window.scrollY > 0) return;
        const diff = event.touches[0].clientY - this.touchStartY;
        if (diff > 0) {
            event.preventDefault();
            // Rubber-band: fast response at start, resistance increases as it stretches
            const max = this.PULL_THRESHOLD + 30;
            this.pullDistance = max * (1 - Math.exp(-diff / (max * 1.2)));
            this.isPulling = true;
            this.cdr.markForCheck();
        }
    }

    @HostListener('touchend')
    onTouchEnd() {
        if (!this.isTouchActive) return;
        this.isTouchActive = false;
        const shouldRefresh = this.readyToRefresh;
        // Snap back with CSS transition
        this.isSnapping = true;
        this.pullDistance = 0;
        this.isPulling = false;
        this.cdr.markForCheck();
        setTimeout(() => {
            this.isSnapping = false;
            this.cdr.markForCheck();
        }, 380);
        if (shouldRefresh) {
            this.lastLoadKey = '';
            this.loadOrdersByActiveTab();
        }
    }

    toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
    }

    closeMenu() {
        this.isMenuOpen = false;
    }

    goToNewOS() {
        this.router.navigate(['/new-os']);
    }

    get canApproveOs(): boolean {
        return this.authService.canApproveOs();
    }

    goToOsApproval() {
        this.router.navigate(['/os-approval']);
    }

    submitSearch() {
        const search = this.searchControl.value.trim();
        if (search.length > 0 && search.length < 3) {
            return;
        }

        this.loadOrdersByActiveTab(1, 20, search);
    }

    setStatusFilter(status: string) {
        if (this.activeStatusFilter === status) {
            return;
        }

        this.activeStatusFilter = status;
        this.loadOrdersByActiveTab(1, this.defaultPageSize, this.searchControl.value.trim(), false, false);
    }

    setActiveTab(tab: 'abertos' | 'ultima-semana' | 'rascunhos') {
        if (this.activeTab === tab) {
            return;
        }

        this.activeTab = tab;
        this.loadOrdersByActiveTab();
    }

    get hasActiveSearch(): boolean {
        return this.searchControl.value.trim().length >= 3;
    }

    get isSearching(): boolean {
        return this.hasActiveSearch && (this.isSearchPending || this.isLoadingOrders);
    }

    get statusFilters(): string[] {
        return this.availableStatusFilters;
    }

    get greetingName(): string {
        const fullName = (this.authService.getUserName() || '').trim();
        if (!fullName) {
            return '';
        }

        return fullName.split(/\s+/)[0] || '';
    }

    get filialName(): string {
        return this.authService.getEmpresaDescricao() || 'Oficina';
    }

    @HostListener('window:scroll')
    onWindowScroll() {
        if (this.isLoadingOrders || this.isLoadingMore || !this.hasNextPage) {
            return;
        }

        const scrollPosition = window.innerHeight + window.scrollY;
        const fullHeight = document.documentElement.scrollHeight;
        if (fullHeight - scrollPosition > 120) {
            return;
        }

        this.loadOrdersByActiveTab(this.currentPage + 1, this.defaultPageSize, undefined, true, false);
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
        this.loadOrdersSubscription?.unsubscribe();
        window.removeEventListener('touchmove', this.touchMoveHandler);
    }

    private loadOrdersByActiveTab(
        page = 1,
        pageSize = this.defaultPageSize,
        overrideSearch?: string,
        append = false,
        resetStatusFilter = true) {
        const search = (overrideSearch ?? this.searchControl.value).trim();
        const status = search && this.activeStatusFilter !== 'Todos' ? this.activeStatusFilter : '';
        const loadKey = `${this.activeTab}:${page}:${pageSize}:${search}:${status}`;
        if (loadKey === this.lastLoadKey && !this.isLoadingOrders && !this.isLoadingMore) {
            this.isSearchPending = false;
            return;
        }

        this.lastLoadKey = loadKey;
        this.loadOrdersSubscription?.unsubscribe();
        this.isLoadingOrders = !append;
        this.isLoadingMore = append;
        this.loadOrdersError = '';

        if (!append) {
            this.scrollToTop();
            this.currentPage = 1;
            this.hasNextPage = false;
            this.serviceOrders = [];
            this.filteredOrders = [];
            if (resetStatusFilter) {
                this.activeStatusFilter = 'Todos';
                this.availableStatusFilters = ['Todos'];
            }
        }

        const request$ = search
            ? this.serviceOrderApiService.search(search, page, pageSize, status)
            : this.activeTab === 'ultima-semana'
                ? this.serviceOrderApiService.listLastWeek(page, false, pageSize)
                : this.activeTab === 'rascunhos'
                    ? this.serviceOrderApiService.listRascunhos(page, false, pageSize)
                    : this.serviceOrderApiService.list(page, false, pageSize);

        this.loadOrdersSubscription = request$
            .pipe(take(1))
            .subscribe({
                next: (result) => {
                    const mappedItems = (result.items || []).map(item => this.mapApiOrderToCard(item));
                    this.currentPage = result.page || page;
                    this.hasNextPage = !!result.hasNextPage;
                    this.serviceOrders = append
                        ? [...this.serviceOrders, ...mappedItems]
                        : mappedItems;
                    if (search && this.activeStatusFilter === 'Todos') {
                        const mergedStatuses = Array.from(new Set(
                            this.serviceOrders
                                .map(order => (order.status || '').trim())
                                .filter(orderStatus => orderStatus.length > 0)
                        ));
                        this.availableStatusFilters = ['Todos', ...mergedStatuses];
                    }
                    this.filteredOrders = [...this.serviceOrders];
                    this.isLoadingOrders = false;
                    this.isLoadingMore = false;
                    this.isSearchPending = false;
                    this.cdr.markForCheck();
                },
                error: (error: unknown) => {
                    if (!append) {
                        this.serviceOrders = [];
                        this.filteredOrders = [];
                    }
                    this.loadOrdersError = this.getLoadOrdersErrorMessage(error);
                    this.isLoadingOrders = false;
                    this.isLoadingMore = false;
                    this.isSearchPending = false;
                    this.lastLoadKey = '';
                    this.cdr.markForCheck();
                }
            });
    }

    navigateToOrder(order: ServiceOrder): void {
        if (this.isDraftOrder(order)) {
            // Pass documentoId via both navigation state (reliable) and
            // sessionStorage (safety net if state is lost across guard redirects)
            sessionStorage.setItem('pendingDocumentoId', String(order.documentoId));
            this.router.navigate(['/new-os'], { state: { documentoId: order.documentoId } });
        } else {
            this.router.navigate(['/os-detail', order.documentoId]);
        }
    }

    private mapApiOrderToCard(item: ServiceOrderListItemDto): ServiceOrder {
        const plate = (item.plate || '').trim();
        const vehicleName = [item.marca, item.vehicle].filter(Boolean).join(' ').trim();

        return {
            documentoId: item.documentoId,
            id: plate || `OS #${item.numero}`,
            vehicle: vehicleName || 'Veículo não identificado',
            year: item.vehicleYear ?? null,
            client: item.client || 'Não informado',
            technician: item.mechanicName || item.sellerName || 'Não atribuído',
            status: item.status || 'Aberto',
            rawStatus: item.status || 'Aberto',
            issuedAt: item.dtHrEmissao || null
        };
    }

    private getOrdersByTab(): ServiceOrder[] {
        return this.serviceOrders;
    }

    private getLoadOrdersErrorMessage(error: unknown): string {
        if (error && typeof error === 'object' && 'name' in error && error.name === 'TimeoutError') {
            return 'Tempo limite excedido ao carregar as ordens de servico.';
        }

        if (error instanceof HttpErrorResponse) {
            if (error.status === 401 || error.status === 403) {
                return 'Sessao invalida para listar ordens de servico.';
            }

            if (error.status > 0) {
                return `Erro ${error.status} ao carregar ordens de servico.`;
            }
        }

        return 'Nao foi possivel carregar as ordens de servico.';
    }

    private isDraftOrder(order: ServiceOrder): boolean {
        if (this.activeTab === 'rascunhos' && !this.hasActiveSearch) {
            return true;
        }

        const normalizedStatus = (order.rawStatus || order.status || '').toLowerCase().trim();
        return normalizedStatus.includes('rascunho');
    }

    private scrollToTop(): void {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }
}
